import { useEffect, useState } from 'react'

import { useLocation, useNavigate } from 'react-router-dom'

import { ROUTES } from '../constants'
import {
  buildUserStatusByBranchId,
  getSelectedBranch,
  getUserDetails,
  isBranchInactiveForUser,
} from '../utils'

// Routes that must remain reachable even when no branch is selected — they
// are the recovery / onboarding surfaces (profile, change-password, branch
// switcher). Skipping the no-branch redirect here is also what breaks the
// redirect loop a user with an incomplete profile would otherwise hit:
//
//   /branch-selection  →  ProfileCompletionGuard → /profile
//                                       ↑                  ↓
//                                  BranchAccessGuard (no branch)
//
// Without the skip, the two guards bounce the URL between
// /branch-selection and /profile forever.
const ACCOUNT_ONLY_PATHS = [ROUTES.PROFILE, ROUTES.CHANGE_PASSWORD, ROUTES.BRANCH]

// Redirects to /branch-selection when the currently selected branch is
// inactive for the logged-in user. Runs on every focus/storage change so the
// guard stays in sync with the cached `/me` payload without a hard refresh.
function BranchAccessGuard({ children }) {
  const navigate = useNavigate()
  const location = useLocation()
  const [renderChildren, setRenderChildren] = useState(true)

  useEffect(() => {
    const check = () => {
      // Always render recovery / onboarding routes so the user can finish
      // their setup. Skipping this check is what breaks the
      // /branch-selection ↔ /profile redirect loop described above.
      if (ACCOUNT_ONLY_PATHS.includes(location.pathname)) {
        setRenderChildren(true)
        return
      }

      const selected = getSelectedBranch()
      if (!selected) {
        navigate(ROUTES.BRANCH_SELECTION, {
          replace: true,
          state: { attemptedPath: location.pathname },
        })
        return
      }
      const details = getUserDetails() || {}
      const statusByBranchId = buildUserStatusByBranchId(details)
      const branch = {
        id: selected.id ?? selected.uuid,
        status: statusByBranchId[selected.id] || selected.status || '',
      }
      if (isBranchInactiveForUser(branch)) {
        navigate(ROUTES.BRANCH_SELECTION, {
          replace: true,
          state: { branchInactive: true, attemptedPath: location.pathname },
        })
        return
      }
      setRenderChildren(true)
    }
    check()
    window.addEventListener('focus', check)
    window.addEventListener('storage', check)
    return () => {
      window.removeEventListener('focus', check)
      window.removeEventListener('storage', check)
    }
  }, [navigate, location.pathname])

  return renderChildren ? children : null
}

export { BranchAccessGuard }
export default BranchAccessGuard
